<template>
	<TransitionRoot as="div" :show="open">
		<Dialog as="div" class="relative z-50" data-testid="slideover" @close="close">
			<TransitionChild
				as="div"
				enter="ease-in-out duration-500"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="ease-in-out duration-500"
				leave-from="opacity-100"
				leave-to="opacity-0">
				<div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
			</TransitionChild>

			<div class="fixed inset-0 overflow-hidden">
				<div class="absolute inset-0 right-0 overflow-hidden">
					<div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
						<TransitionChild
							as="div"
							enter="transform transition ease-in-out duration-500 sm:duration-700"
							enter-from="translate-x-full"
							enter-to="translate-x-0"
							leave="transform transition ease-in-out duration-500 sm:duration-700"
							leave-from="translate-x-0"
							leave-to="translate-x-full">
							<DialogPanel
								class="pointer-events-auto w-screen h-screen transition-all duration-200 ease-in origin-right"
								:class="{
									'max-w-2xl': !expanded,
									'max-w-7xl': expanded
								}"
								data-testid="slideover-panel">
								<div
									class="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
									<div
										class="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
										<div class="px-4 sm:px-6">
											<div class="flex items-center justify-between">
												<DialogTitle
													v-if="title"
													data-testid="title"
													class="text-base font-semibold leading-6 text-gray-900">
													{{ title }}
												</DialogTitle>

												<div class="ml-3 flex h-7 items-center">
													<button
														v-if="expandable"
														type="button"
														tabindex="-1"
														autofocus="false"
														class="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 hidden sm:flex focus:ring-mabey-950 mr-2"
														data-testid="expand"
														@click="expanded = !expanded">
														<span class="absolute -inset-2.5" />
														<span class="sr-only">Expand panel</span>
														<ArrowsPointingOutIcon
															v-if="!expanded"
															class="h-5 w-5"
															aria-hidden="true" />
														<ArrowsPointingInIcon
															v-else
															class="h-5 w-5"
															aria-hidden="true" />
													</button>

													<button
														type="button"
														tabindex="0"
														autofocus="true"
														class="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-mabey-950"
														data-testid="close-icon"
														@click="close">
														<span class="absolute -inset-2.5" />
														<span class="sr-only">Close panel</span>
														<XMarkIcon
															class="h-6 w-6"
															aria-hidden="true" />
													</button>
												</div>
											</div>
										</div>
										<div class="relative mt-6 flex-1 px-4 sm:px-6">
											<slot></slot>
										</div>
									</div>
									<div class="flex flex-shrink-0 justify-end px-4 py-4">
										<app-button
											class="mr-auto"
											theme="outline"
											tabindex="0"
											data-testid="close-button"
											@click="close">
											Close
										</app-button>

										<div v-if="$slots.action" class="flex flex-1">
											<slot name="action"></slot>
										</div>

										<app-button
											v-for="(action, index) in actions"
											:key="index"
											tabindex="0"
											:to="action.url"
											:disabled="action.disabled"
											:icon="action.icon"
											:theme="action.theme"
											class="ml-3"
											data-testid="action"
											@click="action.method">
											{{ action.text }}
										</app-button>
									</div>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script lang="ts" setup>
	import { ref } from 'vue'
	import {
		Dialog,
		DialogPanel,
		DialogTitle,
		TransitionChild,
		TransitionRoot
	} from '@headlessui/vue'
	import {
		XMarkIcon,
		ArrowsPointingOutIcon,
		ArrowsPointingInIcon
	} from '@heroicons/vue/24/outline'

	export interface Action {
		text: string
		url?: string
		icon?: string
		theme?: string
		disabled?: boolean
		method?: Function
	}

	withDefaults(
		defineProps<{
			title?: string
			open: boolean
			actions?: Array<Action>
			expandable?: boolean
		}>(),
		{
			title: undefined,
			actions: undefined,
			open: false,
			expandable: false
		}
	)

	const expanded = ref()

	// Events
	const emit = defineEmits(['close'])
	function close() {
		emit('close')
	}
</script>
